/**
 * Master List Store
 * Zustand store that loads all master list data once and caches it.
 * Consumed by useWorkMasterList hook across all forms and pages.
 */

import { create } from 'zustand';
import { mastersApi } from '../services/wmsService';
import type {
  WorkType,
  WorkCategory,
  WorkNature,
  Scheme,
  FinancialYear,
  Department,
  WorkStatus,
  Zone,
  Ward,
  ProposalSource,
  ProposalType,
  PriorityLevel,
  Designation,
} from '../lib/masterTypes';

interface MasterListState {
  // Data
  workTypes: WorkType[];
  workCategories: WorkCategory[];
  workNatures: WorkNature[];
  schemes: Scheme[];
  financialYears: FinancialYear[];
  departments: Department[];
  workStatuses: WorkStatus[];
  zones: Zone[];
  wards: Ward[];
  proposalSources: ProposalSource[];
  proposalTypes: ProposalType[];
  priorityLevels: PriorityLevel[];
  designations: Designation[];
  eventTags: Array<{ evnt_tag_id: number; evnt_tag_cd: string; evnt_tag_nm: string }>;
  pushkaraluActivities: Array<{ pushkaralu_actvty_id: number; pushkaralu_actvty_cd: string; pushkaralu_actvty_nm: string }>;
  // Finance masters, sourced from the legacy ERP finance module rather than ds-wms.
  // ProposalForm reads these four for its Budgetary Details section; before they were
  // loaded here it fell back to `(masterStore as any).x ?? []`, leaving those required
  // dropdowns permanently empty.
  fundingAgencies: Array<{ fndg_agncy_id: number; fndg_agncy_cd: string; fndg_agncy_nm: string }>;
  functionCenters: Array<{ fnctn_id: number; fnctn_cd: string; fnctn_nm: string }>;
  budgetHeads: Array<{ bdgt_hd_id: number; bdgt_hd_cd: string; bdgt_hd_nm: string }>;
  subSchemes: Array<{ sub_schme_id: number; sub_schme_cd: string; sub_schme_nm: string }>;

  // Status
  loading: boolean;
  loaded: boolean;

  // Actions
  loadAll: () => Promise<void>;
  reload: () => Promise<void>;
}

export const useMasterListStore = create<MasterListState>()((set, get) => ({
  // Data — initialized as empty arrays
  workTypes: [],
  workCategories: [],
  workNatures: [],
  schemes: [],
  financialYears: [],
  departments: [],
  workStatuses: [],
  zones: [],
  wards: [],
  proposalSources: [],
  proposalTypes: [],
  priorityLevels: [],
  designations: [],
  eventTags: [],
  pushkaraluActivities: [],
  fundingAgencies: [],
  functionCenters: [],
  budgetHeads: [],
  subSchemes: [],

  // Status
  loading: false,
  loaded: false,

  loadAll: async () => {
    const { loaded, loading } = get();
    if (loaded || loading) return;

    set({ loading: true });

    const results = await Promise.allSettled([
      mastersApi.getWorkTypesList(),                          // 0
      mastersApi.getWorkCategoriesList(),                     // 1
      mastersApi.getSchemesList(),                             // 2
      mastersApi.getFinancialYearsList(),                      // 3
      mastersApi.getWorkNaturesList(),                           // 4
      mastersApi.getDepartmentsList(),                         // 5
      mastersApi.getWorkStatusesList(),                         // 6
      mastersApi.getZones(),                                   // 7
      mastersApi.getWards(),                                   // 8
      mastersApi.getProposalSourcesList(),                      // 9
      mastersApi.getProposalTypesList(),                       // 10
      (mastersApi as any).getPriorityLevels?.() ?? [],         // 11
      mastersApi.getDesignations(),                            // 12
      mastersApi.getEventTags(),                               // 13
      mastersApi.getPushkaraluActivities(),                    // 14
      // Finance module masters — /apiv1/finance/*, permission-guarded. allSettled
      // below degrades a 403 or an outage to [] rather than failing the whole load.
      mastersApi.getFundingAgencies(),                         // 15
      mastersApi.getFunctionCenters(),                         // 16
      mastersApi.getBudgetHeads(),                             // 17
      mastersApi.getSubSchemes(),                              // 18
    ]);

    const val = (i: number) => {
      const r = results[i];
      return r.status === 'fulfilled' ? (r.value ?? []) : [];
    };

    set({
      workTypes: val(0),
      workCategories: val(1),
      schemes: val(2),
      financialYears: val(3),
      workNatures: val(4),
      departments: val(5),
      workStatuses: val(6),
      zones: val(7),
      wards: val(8),
      proposalSources: val(9),
      proposalTypes: val(10),
      priorityLevels: val(11),
      designations: val(12),
      eventTags: val(13),
      pushkaraluActivities: val(14),
      fundingAgencies: val(15),
      functionCenters: val(16),
      budgetHeads: val(17),
      subSchemes: val(18),
      loading: false,
      loaded: true,
    });
  },

  reload: async () => {
    set({ loaded: false });
    await get().loadAll();
  },
}));
